/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { ImportTicketsService } from '../../services/import-tickets.service';
import { IBoardscanTicketMetadata, ITPTicket, TicketType } from '@touchplan/common';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import ticketValidator from '@touchplan/config/lib/ticket-validators.json';
import {
	activeLineSideValidator,
	ActiveLineValidatorConfig,
	ActiveLineValidatorData,
} from 'src/app/app-data/validators/active-line-side.validator';
import { GET_BOARDSCAN_TICKET_IMAGE_ROUTE } from 'src/app/services/rest-api-configuration/rest-api-routes';

// this _kind of_ works for typed forms when expressed as a generic
// gets squirrely when there's more than one level of depth
export interface IProcessingItem {
	activityName: FormControl<string>;
	plannedStart: FormControl<string>;
	crewSize: FormControl<number>;
	locationId: FormControl<string>;
	roleId: FormControl<string>;
	durationDays: FormControl<number>;
	type: FormControl<string>;
}

export interface IFormalTicket {
	// "original data"
	ticket: ITPTicket<IBoardscanTicketMetadata>;
	// "active data"
	formGroup: FormGroup<IProcessingItem>;
	imageUrl: string;
}

//delibrately _not_ root
@Injectable()
export class TicketCorrectionService {
	private formBuilder = inject(FormBuilder);
	private importTicketsStateService = inject(ImportTicketsStateService);
	private importTicketsService = inject(ImportTicketsService);

	private shutdownSubject$ = new Subject<void>();

	private activeLine$ = this.importTicketsStateService.selectedPlan$.pipe(
		map(
			(plan) =>
				(plan && plan.view.activeLineDate && !(plan.view.activeLineX ?? null)
					? {
							activeLineDate: plan?.view.activeLineDate,
							activeLineX: plan?.view.activeLineX,
					  }
					: null) as ActiveLineValidatorData
		)
	);

	public loadProcessingTickets() {
		return combineLatest([
			this.importTicketsStateService.projectId$,
			this.importTicketsStateService.uploadId$,
			this.importTicketsStateService.roleList,
			this.importTicketsStateService.locationList,
		]).pipe(
			// switchMap(([projectId, uploadId]) => this.importTicketsService.getProcessingTickets('-M7s3MqPjHV-0Isg_k-c', 'steve')), // mock
			filter(([projectId, uploadId]) => !!projectId && !!uploadId), //questionably necessary, probably safer
			switchMap(([projectId, uploadId, roleList, locationList]) =>
				this.importTicketsService.getProcessingTickets(projectId, uploadId).pipe(
					map((tickets) => {
						return tickets.map((ticket) => {
							this.filterOutInvalidId(ticket, 'roleId', roleList);
							this.filterOutInvalidId(ticket, 'locationId', locationList);

							const formGroup = this.makeFormGroup(ticket);
							const imageUrl = this.makeImageUrl(projectId, uploadId, ticket.ticketId);
							return { ticket, formGroup, imageUrl };
						});
					})
				)
			),
			// in theory this will... "clean entirely" when the component is destroyed, store the last value for new subs, and only return one value
			takeUntil(this.shutdownSubject$),
			shareReplay(1),
			take(1)
		);
	}

	private filterOutInvalidId<T>(
		ticket: ITPTicket<IBoardscanTicketMetadata>,
		key: string,
		list: Map<string, T>
	) {
		const id = ticket[key];
		if (!id) {
			return;
		}
		if (!list.has(id)) {
			delete ticket[key];
		}
	}

	public splitProcessingTickets(formalTickets: Array<IFormalTicket>) {
		return formalTickets.reduce(
			(acc, formalTicket) => {
				if (formalTicket.formGroup.invalid) {
					acc.conflictedTickets.push(formalTicket);
				} else {
					acc.resolvedTickets.push(formalTicket);
				}
				return acc;
			},
			{
				conflictedTickets: [] as IFormalTicket[],
				resolvedTickets: [] as IFormalTicket[],
				excludedTickets: [] as IFormalTicket[],
			}
		);
	}

	private makeFormGroup(ticket: ITPTicket): FormGroup<IProcessingItem> {
		const formGroup = this.formBuilder.group<IProcessingItem>({
			activityName: this.formBuilder.control<string>(ticket.activityName, [Validators.required]),

			plannedStart: this.formBuilder.control<string>(ticket.plannedStart, {
				validators: [Validators.required],
				asyncValidators: [
					activeLineSideValidator(this.activeLine$, {
						comparisonMethod: 'date',
						comparisonStyle: '<',
					} as ActiveLineValidatorConfig),
				],
			}),
			crewSize: this.formBuilder.control<number>(ticket.crewSize, [
				Validators.required,
				Validators.min(ticketValidator.liveCrewSize.min),
				Validators.max(ticketValidator.liveCrewSize.max),
			]),
			locationId: this.formBuilder.control<string>(ticket.locationId),
			roleId: this.formBuilder.control<string>(ticket.roleId, [Validators.required]),
			durationDays: this.formBuilder.control<number>(ticket.durationDays, [
				Validators.required,
				Validators.min(ticketValidator.liveDurationDays.min),
				Validators.max(ticketValidator.liveDurationDays.max),
			]),
			type: this.formBuilder.control<string>(ticket.type || TicketType.Task, [Validators.required]),
		});

		//set initial disabled fields as needed
		this.applyTypeChanges(formGroup, ticket.type || TicketType.Task);

		//disable/ enable some fields when the type is changed
		formGroup
			.get('type')
			?.valueChanges.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((type: TicketType) => {
				this.applyTypeChanges(formGroup, type);
			});
		return formGroup;
	}

	// "http://localhost:8081/v1/projects/-MF2CGtecusuotFlUNuf/board-scan/jobs/-OQAYOBlvj1F8EnCJUQJ/tickets/-OQAYZUhiRMkQzq5mv8E/image"
	private makeImageUrl(projectId: string, jobId: string, ticketId: string): string {
		return GET_BOARDSCAN_TICKET_IMAGE_ROUTE(projectId, jobId, ticketId);
	}

	//adjust the form when for it's type specifics
	private applyTypeChanges(formGroup: FormGroup<IProcessingItem>, type: TicketType) {
		switch (type) {
			case TicketType.Milestone:
				formGroup.get('durationDays')?.disable({ emitEvent: false });
				formGroup.get('crewSize')?.disable({ emitEvent: false });
				break;
			case TicketType.Task:
			default:
				formGroup.get('durationDays')?.enable({ emitEvent: false });
				formGroup.get('crewSize')?.enable({ emitEvent: false });
				break;
		}
	}

	/** refresh the formArray to align with a formalTicket source */
	public reloadFormArray(source: IFormalTicket[], formArray: FormArray) {
		// start with the lazy way vs curated insertion/ removals in multiple places
		formArray.clear();
		source.forEach((formalTicket) => {
			formArray.push(formalTicket.formGroup);
		});
	}

	/** generic method for moving tickets between lists and applying the side effects */
	public moveTickets(
		tickets: IFormalTicket[],
		sourceList: BehaviorSubject<IFormalTicket[]>,
		targetList: BehaviorSubject<IFormalTicket[]>,
		sourceFormArray: FormArray,
		targetFormArray?: FormArray // no excluded formArray (yet?)
	): void {
		const sourceValue = sourceList.getValue();
		const targetValue = targetList.getValue();

		// remove from source
		const updatedSource = sourceValue.filter((ticket) => !tickets.includes(ticket));
		// add to target
		const updatedTarget = [...targetValue, ...tickets];

		//apply
		this.reloadFormArray(updatedSource, sourceFormArray);
		if (targetFormArray) {
			this.reloadFormArray(updatedTarget, targetFormArray);
		}
		sourceList.next(updatedSource);
		targetList.next(updatedTarget);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7054
	//may want to be a bit more _advanced_
	public applyUpdateToTicket(formalTicket: IFormalTicket) {
		// milestones don't have durationDays or crewSize in the tpTicket format
		// explictly set to null to overrite the current firebase value
		// long term (excel import?) tpTicket probably wants a common'd class for creation
		const extraData: { durationDays?: number | null; crewSize?: number | null } =
			formalTicket.formGroup.get('type')?.value === TicketType.Milestone
				? { durationDays: null, crewSize: null }
				: {};

		return {
			...formalTicket.ticket,
			...formalTicket.formGroup.value,
			...extraData,
		} as ITPTicket<IBoardscanTicketMetadata>;
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7054

	public saveTicketsFromForm(
		resolvedTickets: IFormalTicket[],
		excludedTickets: IFormalTicket[],
		projectId: string,
		jobId: string
	) {
		const ticketsToSave = resolvedTickets.map((ticketish) => this.applyUpdateToTicket(ticketish));
		const ticketsToSkip = excludedTickets.map((t) => t.ticket);

		return this.importTicketsService.setProcessingTickets(projectId, jobId, ticketsToSave, ticketsToSkip);
	}

	ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
